import React, { useEffect, useMemo, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from './TivigoDialog';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AllInboxRoundedIcon from '@mui/icons-material/AllInboxRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import HeadsetMicRoundedIcon from '@mui/icons-material/HeadsetMicRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import MarkEmailUnreadRoundedIcon from '@mui/icons-material/MarkEmailUnreadRounded';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import TelegramIcon from '@mui/icons-material/Telegram';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  openSupheroChat,
  TELEGRAM_SUPPORT_URL,
  WHATSAPP_SUPPORT_URL,
} from './supportChannels';
import UserPanelTableLoading from './UserPanelTableLoading';
import './userPanelSupport.css';

const PAGE_SIZE = 5;

const CATEGORY_OPTIONS = Object.freeze([
  'Satış Öncesi',
  'Ürün İadesi',
  'Şikayet Talebi',
  'Teknik Konular',
]);

const PRIORITY_OPTIONS = Object.freeze(['Düşük', 'Normal', 'Yüksek', 'Acil']);

const FILTERS = Object.freeze([
  { id: 'all', label: 'Tüm Talepler', shortLabel: 'Tümü', Icon: AllInboxRoundedIcon },
  { id: 'open', label: 'Açık', shortLabel: 'Açık', Icon: ForumRoundedIcon },
  { id: 'waiting', label: 'Beklemede', shortLabel: 'Beklemede', Icon: ScheduleRoundedIcon },
  { id: 'answered', label: 'Cevaplandı', shortLabel: 'Cevaplandı', Icon: MarkEmailUnreadRoundedIcon },
  { id: 'closed', label: 'Çözüldü', shortLabel: 'Çözüldü', Icon: TaskAltRoundedIcon },
]);

const STATUS_META = Object.freeze({
  open: { label: 'Açık' },
  waiting: { label: 'Beklemede' },
  answered: { label: 'Cevaplandı' },
  closed: { label: 'Çözüldü' },
});

const INITIAL_FORM = Object.freeze({
  category: 'Teknik Konular',
  priority: 'Normal',
  title: '',
  message: '',
});

const toBoolean = (value) => value === true || value === 1 || value === '1';

const toDate = (value) => {
  if (value instanceof Date) return value;
  const numericValue = Number(value);
  const date = Number.isFinite(numericValue) && String(value).trim() !== ''
    ? new Date(numericValue > 100000000000 ? numericValue : numericValue * 1000)
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = toDate(value);
  if (!date) return 'Tarih bilgisi yok';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatRelativeDate = (value) => {
  const date = toDate(value);
  if (!date) return 'Tarih bilgisi yok';
  const difference = Math.max(0, Date.now() - date.getTime());
  const minutes = Math.floor(difference / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'Şimdi';
  if (minutes < 60) return `${minutes} dakika önce`;
  if (hours < 24) return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  if (days === 1) return `Dün ${date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
  if (days < 7) return `${days} gün önce`;
  return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const normalizeReply = (reply, index, ticketId) => ({
  ...reply,
  id: reply?.id ?? `${ticketId}-reply-${index + 1}`,
  message: String(reply?.message || ''),
  date: reply?.date ?? reply?.created_at ?? null,
  admin_reply: reply?.admin_reply ?? reply?.is_admin ?? 0,
  read_by_admin: reply?.read_by_admin ?? reply?.is_read ?? 0,
  edited: reply?.edited ?? 0,
});

const normalizeTicket = (ticket, index = 0) => {
  const id = ticket?.id ?? ticket?.ticket_id ?? index;
  const status = STATUS_META[ticket?.status] ? ticket.status : 'open';
  const rawReplies = Array.isArray(ticket?.replies) ? ticket.replies : [];
  const replies = rawReplies.length > 0
    ? rawReplies.map((reply, replyIndex) => normalizeReply(reply, replyIndex, id))
    : ticket?.message
      ? [normalizeReply({ message: ticket.message, admin_reply: 0, date: ticket.created_at }, 0, id)]
      : [];

  return {
    ...ticket,
    id,
    title: String(ticket?.title || ticket?.subject || '—'),
    category: String(ticket?.category || '—'),
    priority: String(ticket?.priority || '—'),
    created_at: ticket?.created_at ?? ticket?.date ?? null,
    status,
    unread_replies: Math.max(0, Number(ticket?.unread_replies) || 0),
    replies,
  };
};

const normalizeTickets = (items) => items.map((ticket, index) => normalizeTicket(ticket, index));

const getStatusLabel = (ticket) => (
  ticket.status === 'answered' && ticket.unread_replies > 0
    ? 'Yeni Yanıt Var'
    : STATUS_META[ticket.status]?.label || 'Bilinmeyen'
);

const getPriorityTone = (priority) => {
  switch (priority) {
    case 'Acil': return 'urgent';
    case 'Yüksek': return 'high';
    case 'Düşük': return 'low';
    default: return 'normal';
  }
};

function SupportFormDropdown({ label, value, options, onChange, formOpen }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);
  const selectedIndex = Math.max(0, options.indexOf(value));

  useEffect(() => {
    if (!formOpen) setOpen(false);
  }, [formOpen]);

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    const frameId = window.requestAnimationFrame(() => optionRefs.current[selectedIndex]?.focus());

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      window.cancelAnimationFrame(frameId);
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open, selectedIndex]);

  const selectOption = (option) => {
    onChange(option);
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const moveOptionFocus = (event, index) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? options.length - 1
        : event.key === 'ArrowDown'
          ? (index + 1) % options.length
          : (index - 1 + options.length) % options.length;
    optionRefs.current[nextIndex]?.focus();
  };

  return (
    <div className={`tvup-support-dropdown${open ? ' is-open' : ''}`} ref={rootRef}>
      <span className="tvup-support-dropdown-label">{label}</span>
      <button
        ref={triggerRef}
        type="button"
        className="tvup-support-dropdown-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
          event.preventDefault();
          setOpen(true);
        }}
      >
        <span>{value}</span>
        <KeyboardArrowDownRoundedIcon aria-hidden="true" />
      </button>
      {open && (
        <div className="tvup-support-dropdown-menu" role="listbox" aria-label={label}>
          {options.map((option, index) => (
            <button
              key={option}
              ref={(node) => { optionRefs.current[index] = node; }}
              type="button"
              role="option"
              aria-selected={value === option}
              className={value === option ? 'is-selected' : undefined}
              onClick={() => selectOption(option)}
              onKeyDown={(event) => moveOptionFocus(event, index)}
            >
              <span>{option}</span>
              {value === option && <CheckRoundedIcon aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function UserPanelSupport({
  tickets,
  loading = false,
  onCreateTicket,
  onReplyTicket,
  onToggleTicket,
  onEditReply,
  onLoadTicket,
}) {
  const [supportTickets, setSupportTickets] = useState(() => (
    Array.isArray(tickets) ? normalizeTickets(tickets) : []
  ));
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ ...INITIAL_FORM });
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [expandedMobileTickets, setExpandedMobileTickets] = useState(() => new Set());
  const [replyMessage, setReplyMessage] = useState('');
  const [replyError, setReplyError] = useState('');
  const [replying, setReplying] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editedMessage, setEditedMessage] = useState('');
  const [editing, setEditing] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!Array.isArray(tickets)) return;
    const incomingTickets = normalizeTickets(tickets);
    setSupportTickets((currentTickets) => incomingTickets.map((ticket) => {
      const currentTicket = currentTickets.find((item) => String(item.id) === String(ticket.id));
      if (ticket.replies.length > 0 || !currentTicket?.replies?.length) return ticket;
      return { ...ticket, replies: currentTicket.replies };
    }));
  }, [tickets]);

  const stats = useMemo(() => supportTickets.reduce((result, ticket) => {
    result.total += 1;
    if (Object.prototype.hasOwnProperty.call(result, ticket.status)) result[ticket.status] += 1;
    return result;
  }, { total: 0, open: 0, waiting: 0, answered: 0, closed: 0 }), [supportTickets]);

  const filteredTickets = useMemo(() => (
    filter === 'all' ? supportTickets : supportTickets.filter((ticket) => ticket.status === filter)
  ), [filter, supportTickets]);

  const sortedTickets = useMemo(() => {
    if (!sortConfig.key) return filteredTickets;

    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    const getSortValue = (ticket) => {
      if (sortConfig.key === 'id') return Number(ticket.id) || 0;
      if (sortConfig.key === 'created_at') return Date.parse(ticket.created_at) || 0;
      if (sortConfig.key === 'status') return getStatusLabel(ticket);
      return ticket[sortConfig.key] || '';
    };

    return [...filteredTickets].sort((firstTicket, secondTicket) => {
      const firstValue = getSortValue(firstTicket);
      const secondValue = getSortValue(secondTicket);

      if (typeof firstValue === 'number' && typeof secondValue === 'number') {
        return (firstValue - secondValue) * direction;
      }

      return String(firstValue).localeCompare(String(secondValue), 'tr', {
        numeric: true,
        sensitivity: 'base',
      }) * direction;
    });
  }, [filteredTickets, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / PAGE_SIZE));
  const visibleTickets = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedTickets.slice(start, start + PAGE_SIZE);
  }, [page, sortedTickets]);

  const selectedTicket = useMemo(() => supportTickets.find(
    (ticket) => String(ticket.id) === String(selectedTicketId),
  ) || null, [selectedTicketId, supportTickets]);

  const activeFilterIndex = Math.max(0, FILTERS.findIndex((item) => item.id === filter));
  const filterTabIndicatorX = [
    '0px',
    'calc(100% + 5px)',
    'calc(200% + 10px)',
    'calc(300% + 15px)',
    'calc(400% + 20px)',
  ][activeFilterIndex];

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    setReplyMessage('');
    setReplyError('');
    setEditingReplyId(null);
    setEditedMessage('');
  }, [selectedTicketId]);

  const selectedReplyCount = selectedTicket?.replies.length || 0;
  useEffect(() => {
    if (!selectedTicketId) return undefined;
    const frameId = window.requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ block: 'end' });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [selectedReplyCount, selectedTicketId]);

  const selectFilter = (nextFilter) => {
    setFilter(nextFilter);
    setPage(1);
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
    setPage(1);
  };

  const updateTicket = (ticketId, updater) => {
    setSupportTickets((currentTickets) => currentTickets.map((ticket) => (
      String(ticket.id) === String(ticketId) ? updater(ticket) : ticket
    )));
  };

  const loadTicketDetail = async (ticketId) => {
    if (typeof onLoadTicket !== 'function') throw new Error('Destek talebi ayrıntı bağlantısı kullanılamıyor.');
    const result = await onLoadTicket(ticketId);
    const detail = result?.data || result || {};
    updateTicket(ticketId, (ticket) => normalizeTicket({
      ...ticket,
      ...(detail.ticket || {}),
      id: detail.ticket?.id ?? ticket.id,
      replies: Array.isArray(detail.replies) ? detail.replies : ticket.replies,
    }));
    return result;
  };

  const openCreateDialog = () => {
    setCreateForm({ ...INITIAL_FORM });
    setCreateError('');
    setCreateOpen(true);
  };

  const closeCreateDialog = () => {
    if (creating) return;
    setCreateOpen(false);
    setCreateError('');
  };

  const openTicketDetail = async (ticketId) => {
    setSelectedTicketId(ticketId);
    try {
      await loadTicketDetail(ticketId);
    } catch (error) {
      setReplyError(error?.message || 'Destek talebi ayrıntıları alınamadı.');
    }
  };

  const toggleMobileTicketDetails = (ticketId) => {
    setExpandedMobileTickets((current) => {
      const next = new Set(current);
      if (next.has(ticketId)) next.delete(ticketId);
      else next.add(ticketId);
      return next;
    });
  };

  const closeTicketDetail = () => {
    if (replying || toggling || editing) return;
    setSelectedTicketId(null);
  };

  const handleCreateChange = (field) => (event) => {
    setCreateForm((current) => ({ ...current, [field]: event.target.value }));
    if (createError) setCreateError('');
  };

  const handleCreateTicket = async (event) => {
    event.preventDefault();
    const title = createForm.title.trim();
    const message = createForm.message.trim();

    if (title.length < 5 || title.length > 100) {
      setCreateError('Konu 5 ile 100 karakter arasında olmalıdır.');
      return;
    }
    if (message.length < 20 || message.length > 2000) {
      setCreateError('Detaylı açıklama 20 ile 2000 karakter arasında olmalıdır.');
      return;
    }

    const payload = {
      category: createForm.category,
      priority: createForm.priority,
      title,
      message,
    };

    setCreating(true);
    setCreateError('');
    try {
      if (typeof onCreateTicket !== 'function') throw new Error('Destek talebi oluşturma bağlantısı kullanılamıyor.');
      await onCreateTicket(payload);
      setFilter('all');
      setPage(1);
      setCreateForm({ ...INITIAL_FORM });
      setCreateOpen(false);
    } catch (error) {
      setCreateError(error?.message || 'Destek talebi oluşturulamadı. Lütfen tekrar deneyin.');
    } finally {
      setCreating(false);
    }
  };

  const handleReplyTicket = async () => {
    if (!selectedTicket || selectedTicket.status === 'closed' || replying) return;
    const message = replyMessage.trim();
    if (message.length < 20 || message.length > 1000) {
      setReplyError('Mesaj 20 ile 1000 karakter arasında olmalıdır.');
      return;
    }

    setReplying(true);
    setReplyError('');
    try {
      const payload = { ticketId: selectedTicket.id, message };
      if (typeof onReplyTicket !== 'function') throw new Error('Destek yanıtı bağlantısı kullanılamıyor.');
      await onReplyTicket(payload);
      setReplyMessage('');
      try {
        await loadTicketDetail(selectedTicket.id);
      } catch (refreshError) {
        setReplyError('Mesaj gönderildi ancak konuşma yeniden yüklenemedi. Talebi kapatıp tekrar açın.');
      }
    } catch (error) {
      setReplyError(error?.message || 'Mesaj gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setReplying(false);
    }
  };

  const handleReplyKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent?.isComposing) return;
    event.preventDefault();
    handleReplyTicket();
  };

  const handleToggleTicket = async () => {
    if (!selectedTicket || toggling) return;
    const status = selectedTicket.status === 'closed' ? 'open' : 'closed';
    setToggling(true);
    setReplyError('');
    try {
      if (typeof onToggleTicket !== 'function') throw new Error('Destek durumu bağlantısı kullanılamıyor.');
      await onToggleTicket({ ticketId: selectedTicket.id, status });
      updateTicket(selectedTicket.id, (ticket) => ({ ...ticket, status, unread_replies: 0 }));
      try {
        await loadTicketDetail(selectedTicket.id);
      } catch (refreshError) {
        setReplyError('Talep durumu güncellendi ancak ayrıntılar yeniden yüklenemedi.');
      }
      setEditingReplyId(null);
      setEditedMessage('');
    } catch (error) {
      setReplyError(error?.message || 'Talep durumu güncellenemedi. Lütfen tekrar deneyin.');
    } finally {
      setToggling(false);
    }
  };

  const canEditReply = (reply) => {
    if (!selectedTicket || selectedTicket.status === 'closed' || toBoolean(reply.admin_reply)) return false;
    const replyDate = toDate(reply.date);
    if (!replyDate) return false;
    const age = Date.now() - replyDate.getTime();
    return age >= 0 && age <= 5 * 60 * 1000;
  };

  const startEditingReply = (reply) => {
    if (!canEditReply(reply)) return;
    setEditingReplyId(reply.id);
    setEditedMessage(reply.message);
    setReplyError('');
  };

  const cancelEditingReply = () => {
    if (editing) return;
    setEditingReplyId(null);
    setEditedMessage('');
  };

  const handleEditReply = async (replyId) => {
    if (!selectedTicket || editing) return;
    const message = editedMessage.trim();
    if (message.length < 20 || message.length > 1000) {
      setReplyError('Düzenlenen mesaj 20 ile 1000 karakter arasında olmalıdır.');
      return;
    }

    setEditing(true);
    setReplyError('');
    try {
      if (typeof onEditReply !== 'function') throw new Error('Destek mesajı düzenleme bağlantısı kullanılamıyor.');
      await onEditReply({ ticketId: selectedTicket.id, replyId, message });
      setEditingReplyId(null);
      setEditedMessage('');
      try {
        await loadTicketDetail(selectedTicket.id);
      } catch (refreshError) {
        setReplyError('Mesaj güncellendi ancak konuşma yeniden yüklenemedi. Talebi kapatıp tekrar açın.');
      }
    } catch (error) {
      setReplyError(error?.message || 'Mesaj güncellenemedi. Lütfen tekrar deneyin.');
    } finally {
      setEditing(false);
    }
  };

  const titleValid = createForm.title.trim().length >= 5 && createForm.title.trim().length <= 100;
  const messageValid = createForm.message.trim().length >= 20 && createForm.message.trim().length <= 2000;
  const replyValid = replyMessage.trim().length >= 20 && replyMessage.trim().length <= 1000;

  return (
    <div className="tvup-support-page">
      <section className="tvup-support-ticket-panel" aria-labelledby="tvup-support-ticket-list-title">
        <header className="tvup-support-panel-heading">
          <div className="tvup-support-panel-title">
            <span className="tvup-support-panel-icon" aria-hidden="true"><ForumRoundedIcon /></span>
            <span>
              <h2 id="tvup-support-ticket-list-title">Destek Taleplerim</h2>
              <p>{filteredTickets.length} talep gösteriliyor</p>
            </span>
          </div>
          <div className="tvup-support-panel-actions">
            <a className="is-whatsapp" href={WHATSAPP_SUPPORT_URL} target="_blank" rel="noreferrer" aria-label="WhatsApp Destek">
              <WhatsAppIcon aria-hidden="true" />
              WhatsApp Destek
            </a>
            <a className="is-telegram" href={TELEGRAM_SUPPORT_URL} target="_blank" rel="noreferrer" aria-label="Telegram Destek">
              <TelegramIcon aria-hidden="true" />
              Telegram Destek
            </a>
            <button className="is-live-chat" type="button" onClick={openSupheroChat} aria-label="Canlı Chat">
              <HeadsetMicRoundedIcon aria-hidden="true" />
              Canlı Chat
            </button>
            <button type="button" onClick={openCreateDialog}><AddRoundedIcon aria-hidden="true" /> Talep Oluştur</button>
          </div>
        </header>

        <div className="tvup-support-filter-scroll">
          <nav
            className="tvup-support-filters"
            role="tablist"
            aria-label="Destek talebi durumları"
            style={{ '--tvup-support-tab-x': filterTabIndicatorX }}
          >
            {FILTERS.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={filter === id}
                className={filter === id ? 'tvup-support-filter-active' : ''}
                onClick={() => selectFilter(id)}
              >
                <Icon />
                <span>{label}</span>
                <small>{id === 'all' ? stats.total : stats[id]}</small>
              </button>
            ))}
          </nav>
        </div>

        <div className="tvup-support-ticket-table" aria-live="polite">
          <div className="tvup-support-ticket-head">
            {[
              ['id', '#ID'],
              ['title', 'Konu'],
              ['category', 'Kategori'],
              ['created_at', 'Talep Tarihi'],
              ['status', 'Durum'],
            ].map(([key, label]) => {
              const isActive = sortConfig.key === key;
              return (
                <span
                  role="columnheader"
                  aria-sort={isActive ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  key={key}
                >
                  <button
                    type="button"
                    className={`tvup-support-sort-button${isActive ? ' is-active' : ''}${isActive && sortConfig.direction === 'asc' ? ' is-ascending' : ''}`}
                    onClick={() => handleSort(key)}
                  >
                    <span>{label}</span>
                    {isActive ? <KeyboardArrowDownRoundedIcon /> : <UnfoldMoreRoundedIcon />}
                  </button>
                </span>
              );
            })}
            <span aria-hidden="true" />
          </div>

          {loading ? (
            <UserPanelTableLoading
              rows={5}
              columns={5}
              label="Destek talepleri yükleniyor"
              className="is-support"
            />
          ) : visibleTickets.length === 0 ? (
            <div className="tvup-support-state tvup-support-state-empty">
              <span className="tvup-support-state-icon"><HeadsetMicRoundedIcon /></span>
              <b>{filter === 'all' ? 'Henüz destek talebiniz yok.' : 'Bu durumda bir talep bulunmuyor.'}</b>
              <span>Yeni bir talep oluşturarak destek ekibimize ulaşabilirsin.</span>
              <button type="button" onClick={openCreateDialog}><AddRoundedIcon /> Yeni Talep Oluştur</button>
            </div>
          ) : (
            <div className="tvup-support-ticket-list">
              {visibleTickets.map((ticket) => {
                const mobileDetailsExpanded = expandedMobileTickets.has(ticket.id);

                return (
                <article
                  key={ticket.id}
                  className={`tvup-support-ticket-row tvup-support-ticket-status-${ticket.status}${mobileDetailsExpanded ? ' is-mobile-expanded' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => openTicketDetail(ticket.id)}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    openTicketDetail(ticket.id);
                  }}
                  aria-label={`#${ticket.id} ${ticket.title} talebini aç`}
                >
                  <span className="tvup-support-ticket-id">#{ticket.id}</span>
                  <span className="tvup-support-ticket-topic">
                    <b>{ticket.title}</b>
                    {ticket.unread_replies > 0 && (
                      <small aria-label="Yeni mesaj" title="Yeni mesaj"><MarkEmailUnreadRoundedIcon /></small>
                    )}
                  </span>
                  <span className="tvup-support-ticket-category"><CategoryRoundedIcon /> {ticket.category}</span>
                  <time className="tvup-support-ticket-date" dateTime={String(ticket.created_at)}>{formatDate(ticket.created_at)}</time>
                  <span className="tvup-support-ticket-status-cell">
                    <span className={`tvup-support-status tvup-support-status-${ticket.status}${ticket.unread_replies > 0 ? ' tvup-support-status-unread' : ''}`}>
                      {getStatusLabel(ticket)}
                    </span>
                  </span>
                  <ChevronRightRoundedIcon className="tvup-support-ticket-arrow" />
                  <button
                    type="button"
                    className="tvup-support-ticket-open"
                    onClick={(event) => {
                      event.stopPropagation();
                      openTicketDetail(ticket.id);
                    }}
                  >
                    <span>Talebi Gör</span>
                    <ChevronRightRoundedIcon />
                  </button>
                  <button
                    type="button"
                    className="tvup-support-ticket-details"
                    aria-expanded={mobileDetailsExpanded}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleMobileTicketDetails(ticket.id);
                    }}
                  >
                    <span>{mobileDetailsExpanded ? 'Detayları Gizle' : 'Detaylar'}</span>
                    <KeyboardArrowDownRoundedIcon />
                  </button>
                </article>
                );
              })}
            </div>
          )}
        </div>

        <footer className="tvup-support-pagination">
          <button
            type="button"
            disabled={loading || page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            <NavigateBeforeRoundedIcon /> Önceki
          </button>
          <span><b>{page}</b> / {totalPages}</span>
          <button
            type="button"
            disabled={loading || page >= totalPages}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
          >
            Sonraki <NavigateNextRoundedIcon />
          </button>
        </footer>
      </section>

      <Dialog
        open={createOpen}
        maxWidth={false}
        keepMounted
        transitionDuration={0}
        disableEscapeKeyDown
        className="tvup-support-dialog tvup-support-create-dialog"
        aria-labelledby="tvup-support-create-title"
        slotProps={{
          backdrop: { className: 'tvup-support-backdrop' },
          paper: { id: 'tvup-support-create-modal', className: 'tvup-support-modal tvup-support-create-modal' },
        }}
      >
        <header className="tvup-support-modal-header">
          <span className="tvup-support-modal-title-icon" aria-hidden="true"><AddRoundedIcon /></span>
          <div>
            <h2 id="tvup-support-create-title">Yeni Destek Talebi</h2>
            <p>Konuyu ve yaşadığın durumu detaylarıyla paylaş.</p>
          </div>
          <button type="button" aria-label="Yeni destek talebi penceresini kapat" disabled={creating} onClick={closeCreateDialog}>
            <CloseRoundedIcon />
          </button>
        </header>

        <form className="tvup-support-create-form" onSubmit={handleCreateTicket}>
          <div className="tvup-support-create-form-content">
            <div className="tvup-support-form-grid">
              <SupportFormDropdown
                label="Kategori"
                value={createForm.category}
                options={CATEGORY_OPTIONS}
                formOpen={createOpen}
                onChange={(category) => setCreateForm((current) => ({ ...current, category }))}
              />
              <SupportFormDropdown
                label="Öncelik"
                value={createForm.priority}
                options={PRIORITY_OPTIONS}
                formOpen={createOpen}
                onChange={(priority) => setCreateForm((current) => ({ ...current, priority }))}
              />
            </div>

            <label className="tvup-support-form-field">
              <span>Konu</span>
              <input
                type="text"
                value={createForm.title}
                maxLength={100}
                aria-invalid={createForm.title.length > 0 && !titleValid}
                onChange={handleCreateChange('title')}
              />
              <small><span>En az 5 karakter</span><b>{createForm.title.length}/100</b></small>
            </label>

            <label className="tvup-support-form-field">
              <span>Detaylı Açıklama</span>
              <textarea
                rows={7}
                value={createForm.message}
                maxLength={2000}
                aria-invalid={createForm.message.length > 0 && !messageValid}
                onChange={handleCreateChange('message')}
              />
              <small><span>En az 20 karakter</span><b>{createForm.message.length}/2000 karakter</b></small>
            </label>

            {createError && <div className="tvup-support-form-error" role="alert"><ErrorOutlineRoundedIcon /> {createError}</div>}
          </div>

          <footer className="tvup-support-modal-actions">
            <button type="button" disabled={creating} onClick={closeCreateDialog}>İptal</button>
            <button type="submit" className="tvup-support-action-primary" disabled={!titleValid || !messageValid || creating}>
              {creating ? <CircularProgress size={17} thickness={5} /> : <SendRoundedIcon />}
              {creating ? 'Gönderiliyor' : 'Gönder'}
            </button>
          </footer>
        </form>
      </Dialog>

      <Dialog
        open={Boolean(selectedTicket)}
        maxWidth={false}
        keepMounted
        transitionDuration={0}
        disableEscapeKeyDown
        className="tvup-support-dialog tvup-support-detail-dialog"
        aria-labelledby="tvup-support-detail-title"
        slotProps={{
          backdrop: { className: 'tvup-support-backdrop' },
          paper: { id: 'tvup-support-detail-modal', className: 'tvup-support-modal tvup-support-detail-modal' },
        }}
      >
        {selectedTicket && (
          <>
            <header className="tvup-support-detail-header">
              <div className="tvup-support-detail-title-row">
                <span className="tvup-support-modal-title-icon" aria-hidden="true"><HeadsetMicRoundedIcon /></span>
                <div>
                  <span><h2 id="tvup-support-detail-title">Destek Talebi #{selectedTicket.id}</h2><span className={`tvup-support-status tvup-support-status-${selectedTicket.status}`}>{getStatusLabel(selectedTicket)}</span></span>
                  <p>{selectedTicket.title}</p>
                </div>
                <button type="button" aria-label="Destek talebi detayını kapat" disabled={replying || toggling || editing} onClick={closeTicketDetail}>
                  <CloseRoundedIcon />
                </button>
              </div>

              <div className="tvup-support-detail-meta">
                <span><CategoryRoundedIcon /> {selectedTicket.category}</span>
                <span className={`tvup-support-detail-priority tvup-support-priority-${getPriorityTone(selectedTicket.priority)}`}><FlagRoundedIcon /> {selectedTicket.priority} Öncelik</span>
                <span><ScheduleRoundedIcon /> {formatDate(selectedTicket.created_at)}</span>
                <button
                  type="button"
                  className={selectedTicket.status === 'closed' ? 'tvup-support-reopen-button' : 'tvup-support-close-ticket-button'}
                  disabled={toggling}
                  onClick={handleToggleTicket}
                >
                  {toggling ? <CircularProgress size={15} thickness={5} /> : selectedTicket.status === 'closed' ? <LockOpenRoundedIcon /> : <LockRoundedIcon />}
                  {selectedTicket.status === 'closed' ? 'Talebi Yeniden Aç' : 'Çözüldü Olarak İşaretle'}
                </button>
              </div>
            </header>

            <section className="tvup-support-conversation" aria-live="polite">
              {selectedTicket.replies.length === 0 ? (
                <div className="tvup-support-conversation-empty"><ForumRoundedIcon /><b>Bu talepte henüz mesaj bulunmuyor.</b></div>
              ) : selectedTicket.replies.map((reply, index) => {
                const adminMessage = toBoolean(reply.admin_reply);
                const ownMessage = !adminMessage;
                const isEditing = String(editingReplyId) === String(reply.id);
                const editable = canEditReply(reply);

                return (
                  <article
                    key={reply.id || `${selectedTicket.id}-${index}`}
                    className={`tvup-support-message ${ownMessage ? 'tvup-support-message-user' : 'tvup-support-message-agent'}`}
                  >
                    <div className="tvup-support-message-label">
                      <b>{ownMessage ? 'Siz' : 'Destek Ekibi'}</b>
                      <time dateTime={String(reply.date)}>{formatRelativeDate(reply.date)}</time>
                    </div>
                    <div className="tvup-support-message-row">
                      {ownMessage && editable && !isEditing && (
                        <button type="button" className="tvup-support-edit-trigger" aria-label="Mesajı düzenle" onClick={() => startEditingReply(reply)}>
                          <EditRoundedIcon />
                        </button>
                      )}
                      <div className="tvup-support-message-bubble">
                        {isEditing ? (
                          <div className="tvup-support-edit-box">
                            <textarea
                              rows={4}
                              value={editedMessage}
                              maxLength={1000}
                              aria-label="Düzenlenen mesaj"
                              onChange={(event) => {
                                setEditedMessage(event.target.value);
                                if (replyError) setReplyError('');
                              }}
                            />
                            <div className="tvup-support-edit-meta"><span>En az 20 karakter</span><b>{editedMessage.length}/1000</b></div>
                            <div className="tvup-support-edit-actions">
                              <button type="button" disabled={editing} onClick={cancelEditingReply}><CloseRoundedIcon /> İptal</button>
                              <button type="button" disabled={editing || editedMessage.trim().length < 20} onClick={() => handleEditReply(reply.id)}>
                                {editing ? <CircularProgress size={14} thickness={5} /> : <CheckRoundedIcon />} Kaydet
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p>{reply.message}</p>
                            <span className="tvup-support-message-foot">
                              {toBoolean(reply.edited) && <small>(düzenlendi)</small>}
                              {ownMessage && (
                                <span
                                  className={toBoolean(reply.read_by_admin) ? 'tvup-support-read' : 'tvup-support-sent'}
                                  title={toBoolean(reply.read_by_admin) ? 'Destek ekibi okudu' : 'Gönderildi'}
                                >
                                  {toBoolean(reply.read_by_admin) ? <DoneAllRoundedIcon /> : <DoneRoundedIcon />}
                                </span>
                              )}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
              <div ref={messagesEndRef} />
            </section>

            {replyError && <div className="tvup-support-detail-error" role="alert"><ErrorOutlineRoundedIcon /> {replyError}</div>}

            {selectedTicket.status === 'closed' ? (
              <footer className="tvup-support-closed-notice">
                <TaskAltRoundedIcon />
                <div>
                  <b>Bu talep çözüldü.</b>
                  <span>Talep durumu “Çözüldü” olduğundan yeni mesaj gönderilemez. Devam etmek için talebi yeniden açabilirsin.</span>
                </div>
              </footer>
            ) : (
              <footer className="tvup-support-reply-area">
                <label>
                  <textarea
                    rows={3}
                    value={replyMessage}
                    maxLength={1000}
                    aria-label="Destek talebine yanıt"
                    aria-invalid={replyMessage.length > 0 && !replyValid}
                    onChange={(event) => {
                      setReplyMessage(event.target.value);
                      if (replyError) setReplyError('');
                    }}
                    onKeyDown={handleReplyKeyDown}
                  />
                  <span><small>Shift + Enter ile yeni satır</small><b>{replyMessage.length}/1000 · Min: 20</b></span>
                </label>
              </footer>
            )}
          </>
        )}
      </Dialog>
    </div>
  );
}

export default UserPanelSupport;
